import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';

import {
    type useGetAllUnviewedNotifications_userNotifications$key,
    type useGetAllUnviewedNotifications_userNotifications$data,
} from './__generated__/useGetAllUnviewedNotifications_userNotifications.graphql.ts';

type NotificationPayload = NonNullable<
    NonNullable<
        NonNullable<useGetAllUnviewedNotifications_userNotifications$data['latest']>['all']
    >[0]
>['payload'];

const unviewedNotificationsFragment = graphql`
    fragment useGetAllUnviewedNotifications_userNotifications on UserNotifications {
        latest {
            all {
                notificationType
                payload {
                    ... on FavoriteNotification {
                        serviceId
                        type
                        unviewedTag {
                            serviceId
                        }
                    }
                    ... on ActivityFeedElement {
                        serviceId
                        type
                        unviewedTag {
                            serviceId
                        }
                    }
                    ... on Conversation {
                        serviceId
                        buyerMember {
                            hasUnviewedTag
                        }
                    }
                }
            }
            messages {
                serviceId
                buyerMember {
                    hasUnviewedTag
                }
            }
        }
        earlier {
            all {
                notificationType
                payload {
                    ... on FavoriteNotification {
                        serviceId
                        type
                        unviewedTag {
                            serviceId
                        }
                    }
                    ... on ActivityFeedElement {
                        serviceId
                        type
                        unviewedTag {
                            serviceId
                        }
                    }
                    ... on Conversation {
                        serviceId
                        buyerMember {
                            hasUnviewedTag
                        }
                    }
                }
            }
        }
    }
`;

export const useGetAllUnviewedNotifications = ({
    userNotifications: userNotificationsRef,
}: {
    userNotifications: useGetAllUnviewedNotifications_userNotifications$key | null;
}): {
    unviewedNotificationsCount: number;
    unviewedNotificationTypes: string[];
    unviewedNotificationIds: {
        unviewedActivityFeedTagIds: string[];
        unviewedItemEventTagIds: string[];
        unviewedConversationIds: string[];
    };
} => {
    const userNotifications = useFragment(unviewedNotificationsFragment, userNotificationsRef);
    const allNotifications = useMemo(
        () => [
            ...(userNotifications?.latest?.all ?? []),
            ...(userNotifications?.earlier?.all ?? []),
        ],
        [userNotifications]
    );

    return useMemo(() => {
        const unviewedNotificationTypes = [];
        const unviewedActivityFeedTagIds = [];
        const unviewedItemEventTagIds = [];

        const unviewedConversationIds: string[] = [];
        const seenConversationIds: Record<string, true> = {};

        const addUnviewedMessage = (message: NotificationPayload): void => {
            if (!message?.buyerMember?.hasUnviewedTag) {
                return;
            }

            unviewedNotificationTypes.push('Message');

            const id = message?.serviceId;

            if (!id || seenConversationIds[id]) {
                return;
            }

            seenConversationIds[id] = true;
            unviewedConversationIds.push(id);
        };

        for (const n of allNotifications) {
            if (!n) {
                continue;
            }

            if (n.notificationType === 'ACTIVITY_FEED') {
                const tag = n.payload?.unviewedTag;

                if (tag?.serviceId) {
                    unviewedActivityFeedTagIds.push(tag.serviceId);
                }

                if (tag && n.payload?.type) {
                    unviewedNotificationTypes.push(n.payload.type);
                }

                continue;
            }

            if (n.notificationType === 'ITEM_EVENT') {
                const tag = n.payload?.unviewedTag;

                if (tag?.serviceId) {
                    unviewedItemEventTagIds.push(tag.serviceId);
                }

                if (tag && n.payload?.type) {
                    unviewedNotificationTypes.push(n.payload.type);
                }

                continue;
            }

            if (n.notificationType === 'MESSAGE') {
                addUnviewedMessage(n.payload);
            }
        }

        // also include messages from Messages tab (dedupe via seenConversationIds)
        for (const message of userNotifications?.latest?.messages ?? []) {
            addUnviewedMessage(message);
        }

        const unviewedNotificationsCount =
            unviewedActivityFeedTagIds.length +
            unviewedItemEventTagIds.length +
            unviewedConversationIds.length;

        return {
            unviewedNotificationsCount,
            unviewedNotificationTypes,
            unviewedNotificationIds: {
                unviewedActivityFeedTagIds,
                unviewedItemEventTagIds,
                unviewedConversationIds,
            },
        };
    }, [allNotifications, userNotifications?.latest?.messages]);
};
